import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import './UserEditModal.css';

interface UserEditModalProps {
  user: {
    id: string;
    name: string;
    initials: string;
    firstName?: string;
    lastName?: string;
  };
  onClose: () => void;
  onSave: (userId: string, data: { firstName: string; lastName: string }) => Promise<void>;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ user, onClose, onSave }) => {
  // Split the name into first and last name if not provided
  const nameParts = user.name.split(' ');
  const initialFirstName = user.firstName || nameParts[0] || '';
  const initialLastName = user.lastName || nameParts.slice(1).join(' ') || '';

  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors: { firstName?: string; lastName?: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      try {
        await onSave(user.id, { firstName, lastName });
        onClose();
      } catch (error) {
        console.error('Error saving user:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="user-edit-modal-overlay" onClick={onClose}>
      <div className="user-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-edit-header">
          <div className="user-edit-header-info">
            <div className="user-edit-avatar">{user.initials}</div>
            <div>
              <h2 className="user-edit-title">Edit User</h2>
              <p className="user-edit-subtitle">Update user information</p>
            </div>
          </div>
          <button className="user-edit-close-btn" onClick={onClose} type="button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-edit-form">
          <div className="user-edit-form-content">
            <div className="user-edit-form-group">
              <label htmlFor="firstName" className="user-edit-label">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                className={`user-edit-input ${errors.firstName ? 'user-edit-input-error' : ''}`}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
              />
              {errors.firstName && <span className="user-edit-error">{errors.firstName}</span>}
            </div>

            <div className="user-edit-form-group">
              <label htmlFor="lastName" className="user-edit-label">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                className={`user-edit-input ${errors.lastName ? 'user-edit-input-error' : ''}`}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
              />
              {errors.lastName && <span className="user-edit-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="user-edit-form-footer">
            <button
              type="button"
              className="user-edit-btn user-edit-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="user-edit-btn user-edit-btn-save"
              disabled={loading}
            >
              <Save size={16} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
